import { clsx } from 'clsx';
import { ReactNode, useMemo } from 'react';

import {
  PressEvent,
  ToggleButton as ReactAriaToggleButton,
} from 'react-aria-components';

import { Link, type LinkProps } from '../Link';
import { Icon } from '@zapier/zinnia-icons';
import type { BetaNavItemKey } from './SecondaryNav';
import { useSecondaryNavContext } from './SecondaryNavContext';
import styles from './SecondaryNavItem.module.scss';
import { BetaSecondaryNavItemContext } from './SecondaryNavItemContext';

type ItemProps = {
  /**
   * What to render in the `BetaSecondaryNavItem`.
   */
  children?: ReactNode;
  /**
   * Optional property for overriding styles.
   */
  className?: string;
  /**
   * unique identifier for this `BetaSecondaryNavItem`
   */
  itemKey: BetaNavItemKey;
  /**
   * Content to display within the `BetaSecondaryNavItem`
   */
  label: ReactNode;
  /**
   * props for the `Link` component within `BetaSecondaryNavItem`.
   * `linkProps.children` is replaced by the `label` prop.
   */
  linkProps: Omit<LinkProps, 'children'>;
};

export const BetaSecondaryNavItem = ({
  children,
  className,
  itemKey,
  label,
  linkProps,
}: ItemProps) => {
  const {
    selectedItemKey,
    expandedSectionKeys,
    registerSelectedItemKey,
    registerExpandedSectionKeys,
  } = useSecondaryNavContext();

  const isSelected = selectedItemKey === itemKey;
  const isExpanded = expandedSectionKeys.includes(itemKey);

  const onPress = (e: PressEvent) => {
    registerSelectedItemKey(itemKey);

    linkProps.onPress?.(e);
  };

  const onToggle = () => {
    registerExpandedSectionKeys(
      isExpanded
        ? expandedSectionKeys.filter((k) => k !== itemKey)
        : [...expandedSectionKeys, itemKey]
    );
  };

  // Unique ID to associate the toggle button with the submenu it controls.
  const submenuId = `${itemKey}-menu`;

  const contextValue = useMemo(
    () => ({
      itemId: itemKey,
      listId: submenuId,
    }),
    [itemKey, submenuId]
  );

  return (
    <BetaSecondaryNavItemContext.Provider value={contextValue}>
      <li className={clsx(styles.navItem, className)} data-zds>
        <div
          className={styles.navItemButton}
          data-selected={isSelected}
          data-zds
        >
          <Link
            {...linkProps}
            aria-current={isSelected ? 'page' : undefined}
            className={clsx(styles.navItemLink, linkProps.className)}
            onPress={onPress}
          >
            {label}
          </Link>

          {children && (
            <ReactAriaToggleButton
              aria-controls={submenuId}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
              className={styles.navItemToggleButton}
              data-expanded={isExpanded}
              data-zds
              isSelected={isExpanded}
              onChange={onToggle}
            >
              <Icon name="arrowSmallDown" size={20} isBlock />
            </ReactAriaToggleButton>
          )}
        </div>

        {children}
      </li>
    </BetaSecondaryNavItemContext.Provider>
  );
};
